/**
 * CTRL_01 Plugin - PII Detection & Masking
 * Custom UI with enhanced PII scanning dashboard
 * Extends GenericConfigPanel with visual PII type selection and masking preview
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Tag,
  Progress,
  Alert,
  Statistic,
  Divider,
  Space,
  Tooltip,
  Badge,
} from 'antd';
import {
  EyeInvisibleOutlined,
  SafetyOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors, spacing } from '../../../../theme/designTokens';

/**
 * PII Type Cards - Visual selection interface
 */
function PIITypeCard({ type, enabled, onToggle, detectionCount = 0 }) {
  const piiTypes = {
    email: {
      icon: '📧',
      name: 'Email Address',
      example: 'user@example.com → u***@example.com',
      risk: 'medium',
    },
    phone: {
      icon: '📞',
      name: 'Phone Number',
      example: '555-1234 → ***-1234',
      risk: 'low',
    },
    ssn: {
      icon: '🔒',
      name: 'Social Security Number',
      example: '123-45-6789 → [REDACTED]',
      risk: 'critical',
    },
    credit_card: {
      icon: '💳',
      name: 'Credit Card',
      example: '4111-****-****-1234',
      risk: 'critical',
    },
    ip_address: {
      icon: '🌐',
      name: 'IP Address',
      example: '192.168.1.1 → [IP_REDACTED]',
      risk: 'low',
    },
    dob: {
      icon: '🎂',
      name: 'Date of Birth',
      example: '1990-01-15 → [DOB]',
      risk: 'medium',
    },
    name: {
      icon: '👤',
      name: 'Person Name',
      example: 'John Doe → [NAME]',
      risk: 'medium',
    },
    address: {
      icon: '🏠',
      name: 'Physical Address',
      example: '123 Main St → [ADDRESS]',
      risk: 'medium',
    },
  };

  const typeInfo = piiTypes[type] || { icon: '📄', name: type, example: '', risk: 'low' };

  // Use Ant Design color names for proper contrast
  const riskColors = {
    critical: 'red',
    high: 'orange',
    medium: 'gold',
    low: 'green',
  };

  return (
    <Card
      size="small"
      hoverable
      onClick={onToggle}
      className={enabled ? 'selected' : ''}
      style={{
        borderWidth: 2,
        borderStyle: 'solid',
        borderColor: enabled ? colors.primary : '#E4E1E8',
        backgroundColor: enabled ? '#F4F1FA' : '#FFFFFF',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        height: '100%',
      }}
      bodyStyle={{ padding: spacing.md, minHeight: 110 }}
    >
      <Row align="middle" gutter={8} wrap={false}>
        <Col flex="none">
          <div style={{ fontSize: 32, lineHeight: 1 }}>{typeInfo.icon}</div>
        </Col>
        <Col flex="auto">
          <div style={{ fontWeight: 600, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
            {typeInfo.name}
            {enabled && (
              <CheckCircleOutlined
                style={{ color: colors.status.pass, fontSize: 16 }}
              />
            )}
          </div>
          <div style={{
            fontSize: 12,
            color: 'rgba(23, 5, 43, 0.65)',
            marginBottom: 4,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}>
            {typeInfo.example}
          </div>
          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
            <Tag color={riskColors[typeInfo.risk]} style={{ fontSize: 10, margin: 0, fontWeight: 600 }}>
              {typeInfo.risk.toUpperCase()}
            </Tag>
            {detectionCount > 0 && (
              <Tag color="blue" style={{ fontSize: 10, margin: 0 }}>
                {detectionCount} detected
              </Tag>
            )}
          </div>
        </Col>
      </Row>
    </Card>
  );
}

/**
 * PII Detection Stats Dashboard
 */
function PIIStatsOverview({ config }) {
  const enabledTypes = config?.pii_types_enabled || [];
  const confidenceThreshold = config?.confidence_threshold || 80;
  const maskingEnabled = config?.mask_before_processing || false;

  // Mock stats for demo
  const totalScans = 1247;
  const piiDetected = 342;
  const maskedEvents = 298;
  const detectionRate = ((piiDetected / totalScans) * 100).toFixed(1);

  return (
    <Row gutter={[spacing.md, spacing.md]}>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="PII Types Monitored"
            value={enabledTypes.length}
            suffix="/ 8"
            prefix={<SafetyOutlined style={{ color: colors.primary }} />}
            valueStyle={{ color: colors.primary }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Detection Rate"
            value={detectionRate}
            suffix="%"
            prefix={<EyeInvisibleOutlined style={{ color: colors.severity.medium }} />}
            valueStyle={{ color: colors.severity.medium }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Confidence Threshold"
            value={confidenceThreshold}
            suffix="%"
            prefix={<SafetyOutlined style={{ color: colors.status.pass }} />}
            valueStyle={{ color: colors.status.pass }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} md={6}>
        <Card>
          <Statistic
            title="Masking Status"
            value={maskingEnabled ? 'Active' : 'Disabled'}
            prefix={
              maskingEnabled ? (
                <CheckCircleOutlined style={{ color: colors.status.pass }} />
              ) : (
                <WarningOutlined style={{ color: colors.severity.medium }} />
              )
            }
            valueStyle={{
              color: maskingEnabled ? colors.status.pass : colors.severity.medium,
              fontSize: 20,
            }}
          />
        </Card>
      </Col>
    </Row>
  );
}

/**
 * CTRL_01 Plugin Component
 */
function CTRL_01_Plugin({ projectId, controlCode }) {
  const [showDashboard, setShowDashboard] = useState(true);
  const [config, setConfig] = useState(null);

  // Listen for config changes from GenericConfigPanel
  useEffect(() => {
    // In production, this would fetch actual config
    // For demo, using mock data
    setConfig({
      enabled: false,
      pii_types_enabled: ['email', 'phone', 'ssn'],
      confidence_threshold: 80,
      mask_before_processing: true,
    });
  }, [projectId, controlCode]);

  // Toggle PII type in config
  const handleTogglePIIType = (type) => {
    setConfig((prev) => {
      const currentTypes = prev.pii_types_enabled || [];
      const isEnabled = currentTypes.includes(type);

      return {
        ...prev,
        pii_types_enabled: isEnabled
          ? currentTypes.filter((t) => t !== type)
          : [...currentTypes, type],
      };
    });
  };

  return (
    <div>
      {showDashboard && config && (
        <>
          <Alert
            message="PII Detection Dashboard"
            description="Enhanced visualization for PII detection configuration. This plugin extends the standard schema-driven config with visual PII type selection and real-time stats."
            type="info"
            showIcon
            closable
            onClose={() => setShowDashboard(false)}
            style={{ marginBottom: spacing.lg }}
          />

          <Card
            title={
              <Space>
                <EyeInvisibleOutlined />
                PII Detection Overview
              </Space>
            }
            style={{ marginBottom: spacing.lg }}
          >
            <PIIStatsOverview config={config} />
          </Card>

          <Divider orientation="left">Visual PII Type Selection</Divider>

          <Alert
            message="Interactive PII Type Cards"
            description="Click cards to toggle PII types. Changes are shown visually here and synced to the config panel below."
            type="success"
            showIcon
            style={{ marginBottom: spacing.md }}
          />

          <Row gutter={[spacing.md, spacing.md]} style={{ marginBottom: spacing.lg }}>
            {['email', 'phone', 'ssn', 'credit_card', 'ip_address', 'dob', 'name', 'address'].map(
              (type) => (
                <Col xs={24} sm={12} md={6} key={type}>
                  <PIITypeCard
                    type={type}
                    enabled={config.pii_types_enabled?.includes(type)}
                    onToggle={() => handleTogglePIIType(type)}
                    detectionCount={Math.floor(Math.random() * 50)}
                  />
                </Col>
              )
            )}
          </Row>

          <Divider />
        </>
      )}

      {/* Standard GenericConfigPanel for actual configuration */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_01_Plugin;
